import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { debounce } from 'lodash';
import { Alert, Button, Card, Descriptions, Empty, message, Select, Space, Spin, Tag, Typography } from 'antd';
import { SwapOutlined } from '@ant-design/icons';
import { companies } from '../../../../../../API/TT_ELD/Users/company';
import { eldBrandLabel, transferErrorMessage } from '../../../../../../API/TT_ELD/companyTransfer';
import { useCompanyData } from '../../../../../../Hooks/TT_ELD/Companies';
import { useRetryTransfer, useTransferStatus } from '../../../../../../Hooks/TT_ELD/CompanyTransfer';
import { useProjectData } from '../../../../../../Hooks/useProjectData';
import useRouteQuery from '../../../../../../Utils/useRouterQuery';
import { DEFAULT_ELD, STANDARD_ELD } from '../../../../../../Utils/eldTypeLabels';
import TransferModal from './TransferModal';
import TransferProgress from './TransferProgress';
import TransferHistory from './TransferHistory';
import { TransferBadge, transferSummary } from './transferLabels';

const { Text, Paragraph } = Typography;

// Who can start / retry transfers (the page itself uses the same roles)
export const COMPANY_TRANSFER_ROLES = ['sales', 'manager'];

const getRole = (): string => {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}')?.role || '';
  } catch {
    return '';
  }
};

// Digits only → USDOT search, anything else → company name search
const useCompanySearch = (term: string) =>
  useQuery(
    ['company-transfer-search', term],
    () => (/^\d+$/.test(term) ? companies.read('', term) : companies.read(term)),
    { enabled: term.trim().length > 1, refetchOnWindowFocus: false, keepPreviousData: true },
  );

const CompanyTransfer = () => {
  const { projectData } = useProjectData();
  const canManage = COMPANY_TRANSFER_ROLES.includes(getRole());

  const [companyId, setCompanyId] = useRouteQuery<string>('', 'companyId', (v) => String(v));
  const [term, setTerm] = useState('');
  const [modalOpen, setModalOpen] = useState(false);

  const debouncedSetTerm = useMemo(() => debounce((value: string) => setTerm(value), 400), []);
  const { data: searchResult, isFetching: searching } = useCompanySearch(term);
  const { data: company, isLoading: companyLoading } = useCompanyData(companyId || undefined);
  const { data: transfer, isLoading: transferLoading } = useTransferStatus(companyId || undefined);
  const retry = useRetryTransfer(companyId || undefined);

  const options = (searchResult?.data ?? []).map((c: any) => ({
    value: String(c.id),
    label: `${c.name}${c.usdot ? ` · USDOT ${c.usdot}` : ''}`,
  }));
  // Keep the chosen company visible in the select even when it isn't in the current results
  if (company && companyId && !options.some((o: any) => o.value === companyId)) {
    options.unshift({ value: companyId, label: `${company.name}${company.usdot ? ` · USDOT ${company.usdot}` : ''}` });
  }

  const eldStatus = company?.eldStatus;
  const isLegal = company?.config?.eldType === 'custom';
  const running = transfer?.status === 'running';
  const blockedReason =
    eldStatus === 'migrated_out'
      ? 'This company has already been transferred out.'
      : eldStatus === 'migrating' || running
        ? 'A transfer of this company is already running.'
        : company && company.isActive === false
          ? 'Only an active company can be transferred.'
          : null;

  const onRetry = async () => {
    try {
      await retry.mutateAsync();
      message.success('Failed drivers were queued again');
    } catch (error) {
      message.error(transferErrorMessage(error));
    }
  };

  return (
    <div className="mainPage">
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card>
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Typography.Title level={4} style={{ margin: 0 }}>
              Company Transfer
            </Typography.Title>
            <Text type="secondary">
              Moves a company from {eldBrandLabel(projectData?.projectName)} to another ELD brand. To switch between{' '}
              {DEFAULT_ELD} and {STANDARD_ELD} on the same ELD, use the company's ELD Migration tab instead.
            </Text>
            <Select
              style={{ width: '100%', maxWidth: 520, marginTop: 8 }}
              showSearch
              allowClear
              filterOption={false}
              placeholder="Search a company by name or USDOT"
              value={companyId || undefined}
              onSearch={debouncedSetTerm}
              onChange={(value) => setCompanyId(value || '')}
              options={options}
              notFoundContent={searching ? <Spin size="small" /> : term.trim().length > 1 ? 'No companies found' : null}
            />
          </Space>
        </Card>

        {!companyId ? (
          <Card>
            <Empty description="Choose a company to see its transfer status" />
          </Card>
        ) : companyLoading || transferLoading ? (
          <Spin style={{ display: 'block', margin: 40 }} />
        ) : (
          <>
            <Card
              title={
                <Space wrap>
                  <Link to={`/companies/${companyId}`}>{company?.name || `Company #${companyId}`}</Link>
                  <TransferBadge eldStatus={eldStatus} />
                </Space>
              }
              extra={
                canManage && (
                  <Button
                    type="primary"
                    icon={<SwapOutlined />}
                    disabled={!!blockedReason}
                    onClick={() => setModalOpen(true)}
                  >
                    Start transfer
                  </Button>
                )
              }
            >
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Descriptions size="small" column={{ xs: 1, sm: 2, md: 4 }}>
                  <Descriptions.Item label="USDOT">{company?.usdot || '-'}</Descriptions.Item>
                  <Descriptions.Item label="Company type">
                    <Tag color={isLegal ? 'blue' : 'default'}>{isLegal ? STANDARD_ELD : DEFAULT_ELD}</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Active">{company?.isActive === false ? 'No' : 'Yes'}</Descriptions.Item>
                  <Descriptions.Item label="Last transfer">
                    {transfer ? transferSummary(transfer) : 'Never transferred'}
                  </Descriptions.Item>
                </Descriptions>
                {blockedReason && canManage && <Alert type="info" showIcon message={blockedReason} />}
                {company?.eldStatusDescription && (
                  <Paragraph type="secondary" style={{ margin: 0 }}>
                    {company.eldStatusDescription}
                  </Paragraph>
                )}
              </Space>
            </Card>

            {transfer && (
              <Card title="Latest transfer">
                <TransferProgress
                  transfer={transfer}
                  canManage={canManage}
                  retrying={retry.isLoading}
                  onRetry={onRetry}
                />
              </Card>
            )}

            <Card title="Transfer history">
              <TransferHistory companyId={companyId} />
            </Card>
          </>
        )}
      </Space>

      {modalOpen && company && (
        <TransferModal companyId={companyId} company={company} onClose={() => setModalOpen(false)} />
      )}
    </div>
  );
};

export default CompanyTransfer;
